import { useEffect, useState } from 'react'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Text } from '../catalyst/text'
import type { FieldSeasonInput } from '../lib/field-seasons'
import type { FieldSeason } from '../lib/types'

/** Edits the per-field, per-season header shared by CanadaGAP Forms H1 and H2. */
export function SeasonHeaderDialog({
  open,
  onClose,
  seasonYear,
  season,
  saveSeason,
}: {
  open: boolean
  onClose: () => void
  seasonYear: number
  season: FieldSeason | null
  saveSeason: (input: FieldSeasonInput) => Promise<void>
}) {
  const [previousCrop, setPreviousCrop] = useState('')
  const [currentCrop, setCurrentCrop] = useState('')
  const [variety, setVariety] = useState('')
  const [datePlanted, setDatePlanted] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPreviousCrop(season?.previousCrop ?? '')
    setCurrentCrop(season?.currentCrop ?? 'Blueberry')
    setVariety(season?.variety ?? '')
    setDatePlanted(season?.datePlanted ?? '')
    setError(null)
  }, [open, season])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await saveSeason({
        previousCrop: previousCrop.trim() || null,
        currentCrop: currentCrop.trim() || null,
        variety: variety.trim() || null,
        datePlanted: datePlanted || null,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save season details.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{seasonYear} season details</DialogTitle>
      <DialogDescription>The production site header on CanadaGAP Forms H1 and H2.</DialogDescription>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Previous year crop(s)</Label>
              <Input value={previousCrop} onChange={(e) => setPreviousCrop(e.target.value)} placeholder="e.g. Blueberry" autoFocus />
            </FormField>
            <FormField>
              <Label>Current crop</Label>
              <Input value={currentCrop} onChange={(e) => setCurrentCrop(e.target.value)} />
            </FormField>
            <FormField>
              <Label>Variety</Label>
              <Input value={variety} onChange={(e) => setVariety(e.target.value)} placeholder="e.g. Duke" />
            </FormField>
            <FormField>
              <Label>Date planted</Label>
              <Input type="date" value={datePlanted} onChange={(e) => setDatePlanted(e.target.value)} />
            </FormField>
          </FieldGroup>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
